/**
 * One saved mental model: open it, keep working on it, rename it.
 *
 * Changes save as you make them. Who can see it is decided by the Space it
 * lives in — your own space, or a team space.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';

import { Modal, TextInput } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { downloadMap } from '@/features/mental-map/download';
import { Legend } from '@/features/mental-map/Legend';
import { MapEditor } from '@/features/mental-map/MapCanvas';
import type { MapDoc } from '@/features/mental-map/model';
import {
  getSavedModel,
  listSaveSpaces,
  saveSavedModel,
  setSavedModelRetired,
  type SavedModel,
  type SaveSpace,
} from '@/features/mental-map/savedModels';

const LAB_PATH = '/elevation/lab/mental-map';
const SAVE_DELAY_MS = 700;

type SaveState = 'saved' | 'saving' | 'failed';

export function MentalModelPage() {
  const { modelId = '' } = useParams<{ modelId: string }>();
  const navigate = useNavigate();
  const [model, setModel] = useState<SavedModel | null>(null);
  const [space, setSpace] = useState<SaveSpace | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const latest = useRef<MapDoc | null>(null);
  const savedJson = useRef('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([getSavedModel(modelId), listSaveSpaces()]).then(([found, spaces]) => {
      if (cancelled) return;
      if (!found.ok || !found.data) {
        setLoadError(found.error ?? 'This model could not be opened.');
        return;
      }
      setModel(found.data);
      setName(found.data.name);
      latest.current = found.data.doc;
      savedJson.current = JSON.stringify(found.data.doc);
      setSpace(spaces.data?.find((s) => s.id === found.data!.spaceId) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  const flush = useCallback(async () => {
    timer.current = null;
    const doc = latest.current;
    if (!doc) return;
    const json = JSON.stringify(doc);
    if (json === savedJson.current) {
      setSaveState('saved');
      return;
    }
    const result = await saveSavedModel({ modelId, doc });
    if (result.ok) {
      savedJson.current = json;
      setSaveState('saved');
      setSaveError(null);
    } else {
      setSaveState('failed');
      setSaveError(result.error ?? 'Your changes could not be saved.');
    }
  }, [modelId]);

  // Leaving with a change still waiting: send it now.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void flush();
      }
    },
    [flush],
  );

  const onChange = useCallback(
    (doc: MapDoc) => {
      latest.current = doc;
      if (JSON.stringify(doc) === savedJson.current) return;
      setSaveState('saving');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), SAVE_DELAY_MS);
    },
    [flush],
  );

  const rename = async () => {
    const next = name.trim();
    if (!model || !next || next === model.name) {
      setName(model?.name ?? '');
      return;
    }
    setSaveState('saving');
    const result = await saveSavedModel({ modelId, name: next });
    if (result.ok) {
      setModel({ ...model, name: next });
      setSaveState('saved');
      setSaveError(null);
    } else {
      setSaveState('failed');
      setSaveError(result.error ?? 'The name could not be changed.');
    }
  };

  const remove = async () => {
    const result = await setSavedModelRetired(modelId, true);
    if (result.ok) navigate(LAB_PATH);
    else {
      setConfirmRemove(false);
      setSaveError(result.error ?? 'The model could not be removed.');
    }
  };

  if (loadError) {
    return (
      <div className="mx-auto max-w-xl px-6 py-16">
        <h1 className="font-heading text-xl font-semibold text-[var(--text-pri)]">This model could not be opened</h1>
        <p className="mt-2 font-body text-sm text-[var(--text-sec)]">{loadError}</p>
        <Link to={LAB_PATH} className="mt-4 inline-block font-body text-sm text-[var(--text-pri)] underline">
          Back to Mental Map
        </Link>
      </div>
    );
  }
  if (!model) {
    return <p className="px-6 py-16 font-body text-sm text-[var(--text-sec)]">Opening the model…</p>;
  }

  const readOnly = space !== null && !space.canSave;

  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-[560px] flex-col bg-[var(--bg-raised)]">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--rule)] px-4 py-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(LAB_PATH)} title="Back to your rooms and saved models">
          <ArrowLeft className="h-4 w-4" /> All models
        </Button>
        <div className="min-w-56 flex-1">
          <TextInput
            aria-label="Model name"
            value={name}
            maxLength={120}
            disabled={readOnly}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => void rename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
        </div>
        <span className="font-body text-xs text-[var(--text-dim)]">
          {space ? (space.kind === 'personal' ? 'In your own space' : `In ${space.name}`) : 'In a space you belong to'}
        </span>
        <span role="status" className={`font-body text-xs ${saveState === 'failed' ? 'text-[var(--coral)]' : 'text-[var(--text-dim)]'}`}>
          {readOnly ? 'View only' : saveState === 'saved' ? 'Saved' : saveState === 'saving' ? 'Saving…' : 'Not saved'}
        </span>
        <Button variant="secondary" size="sm" onClick={() => downloadMap(latest.current ?? model.doc, name.trim() || model.name)}>
          <Download className="h-4 w-4" /> Save as picture
        </Button>
        {!readOnly && (
          <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(true)}>
            Remove
          </Button>
        )}
      </header>
      {saveError && (
        <p role="alert" className="border-b border-[var(--rule)] bg-[var(--surface)] px-4 py-1.5 font-body text-xs text-[var(--coral)]">
          {saveError}
        </p>
      )}
      <div className="min-h-0 flex-1">
        <MapEditor initialDoc={model.doc} onChange={readOnly ? () => undefined : onChange} legend={(api) => <Legend {...api} />} />
      </div>

      <Modal
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        size="sm"
        title="Remove this model?"
        description="It disappears from your list. It is kept, not destroyed, and can be brought back."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmRemove(false)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => void remove()}>
              Remove
            </Button>
          </>
        }
      />
    </div>
  );
}
